import React, {ReactElement, useEffect, useState} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledContainer, StyledHeadingVariant} from "../../shared/StyledComponents";
import {
  BORDER_RADIUS_LARGE,
  DIVIDER_XBLACK,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_SM_WIDTH,
  PD_LG,
  PD_XSM
} from "../../shared/dimens";
import {AccountCircleOutlined, AlternateEmailOutlined, ArticleOutlined, ColorLensOutlined} from "@mui/icons-material";
import {Button, ButtonBase, Grid, IconButton, Tooltip, Typography} from "@mui/material";
import {App} from "../App";
import {nightGray} from "../../shared/colors";
import {Action, UserDisplayName} from "../../shared/types";
import {AvatarPickerFragment} from "../app-shared/avatarmaker/AvatarPickerFragment";
import {ProfileTabsContainer} from "./ProfileTabsContainer";
import {Tabs} from "../../shared/TabsContainer";
import {PathComponent, PathProps} from "../../index";
import {PortfolioFragment} from "./PortfolioFragment";
import {FullscreenContainer} from "../app-shared/common/Components";
import {AboutFragment} from "./AboutFragment";
import {ContactFragment} from "./ContactFragment";
import {ViewAvatarFragment} from "./ViewAvatarFragment";
import {Follow, Follows, UserProfile} from "../types";
import {BaseApp} from "../../shared/BaseApp";
import {Member} from "../../shared/entities";
import {getMemberAuth} from "../../shared/auth";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../../shared/BaseFragment";

export enum BackgroundType {
  COLOR = "color",
  IMAGE = "image",
}

export type Background = {
  id: string,
  type: BackgroundType,
  color?: string,
  imageThumbnail?: string,
  imageUrl?: string,
}

const BACKGROUND_COLORS: string[] = [
  '#fa541c',
  '#faad14',
  '#fadb14',
  '#a0d911',
  '#52c41a',
  '#1890ff',
  '#2f54eb',
  '#722ed1',
  '#eb2f96',
  '#efefef',
  '#a1a1a1',
  '#909090',
  '#606060',
  '#303030',
  '#101010',
];

export const BACKGROUNDS: Background[] = [
  ...Array(78).fill(0).map((_, index) => {
    return {
      id: "image_" + index,
      type: BackgroundType.IMAGE,
      imageUrl: "/assets/backgrounds/images/bg" + (index + 1) + ".svg",
      imageThumbnail: "/assets/backgrounds/thumbs/bg" + (index + 1) + ".png",
    }
  }),
  ...BACKGROUND_COLORS.map((color, index) => {
    return {
      id: "color_" + index,
      type: BackgroundType.COLOR,
      color: color,
    }
  }),
];

export enum ProfileTabType {
  PORTFOLIO = "portfolio",
  ABOUT = "about",
  CONTACT = "contact",
}

export const TABS: Tabs<ProfileTabType> = {
  items: [
    {
      type: ProfileTabType.PORTFOLIO,
      path: "portfolio",
      text: "Portfolio",
      iconType: ArticleOutlined,
      render: pathProps => <PortfolioFragment path={pathProps}/>,
    },
    {
      type: ProfileTabType.ABOUT,
      path: "about",
      text: "About",
      iconType: AccountCircleOutlined,
      render: pathProps => <AboutFragment path={pathProps}/>,
    },
    {
      type: ProfileTabType.CONTACT,
      path: "contact",
      text: "Contact",
      iconType: AlternateEmailOutlined,
      render: pathProps => <ContactFragment path={pathProps}/>,
    },
  ],
  containerId: "profile",
};

function BackgroundView(props: {
  initialBackground: Background,
  onChange: (background: Background) => void,
}): ReactElement {
  const [backgroundIndex, setBackgroundIndex] = useState<number>(BACKGROUNDS.findIndex(bg => bg === props.initialBackground));
  useEffect(() => {
    props.onChange(BACKGROUNDS[backgroundIndex]);
  }, [backgroundIndex]);
  return <StyledBoxColumn
    style={{width: PAGE_FRAGMENT_HALF_WIDTH, padding: PD_LG, height: PAGE_FRAGMENT_SM_WIDTH, overflowY: "scroll"}}>
    <Typography style={{fontWeight: "bold"}}>Background</Typography>
    <Grid container>
      {BACKGROUNDS.map((bg, index) => <Grid xs={4}><ButtonBase
        onClick={() => setBackgroundIndex(index)}
        style={{
          boxSizing: "border-box",
          border: index === backgroundIndex ? DIVIDER_XBLACK : null,
          width: "100%",
          aspectRatio: 2,
          padding: PD_XSM
        }}>
        {bg?.type === BackgroundType.IMAGE
          ? <img src={bg.imageThumbnail} style={{width: "100%", height: "100%", objectFit: "cover"}}/>
          : <div style={{width: "100%", height: "100%", backgroundColor: bg.color}}/>}
      </ButtonBase></Grid>)}
    </Grid>
  </StyledBoxColumn>
}

export type ProfileFragmentProps = BaseFragmentProps & {
  userProfile: UserProfile,
}

type ProfileFragmentState = BaseFragmentState & {
  background: Background,
}

function ProfileCoverView(props: {
  path: PathProps,
  userProfile: UserProfile,
  onViewAvatar: () => void,
  onChangeAvatar: () => void,
}): ReactElement {
  const member = props.userProfile.member;
  const isMe = getMemberAuth()?.getMemberId() === member.memberId;
  const [follow, setFollow] = useState<Follow>();
  const [followingMembers, setFollowingMembers] = useState<Member[]>();
  const [followerMembers, setFollowerMembers] = useState<Member[]>();
  // useEffect(() => {
  //   (async function () {
  //     setFollow(await Follows.getInstance().loadListItem(member.memberId));
  //     const follows = await Follows.getMemberInstance(member.memberId).getOrLoadListItems();
  //     const followingMembers: Member[] = [];
  //     for (const follow of follows) {
  //       followingMembers.push(await Members.getInstance().getOrLoadMember(follow.id));
  //     }
  //     setFollowingMembers(followingMembers);
  //     const followers = await Followers.getMemberInstance(member.memberId).getOrLoadListItems();
  //     const followerMembers: Member[] = [];
  //     for (const follower of followers) {
  //       followerMembers.push(await Members.getInstance().getOrLoadMember(follower));
  //     }
  //     setFollowerMembers(followerMembers);
  //   })();
  // }, [setFollow, member, setFollowingMembers, setFollowerMembers]);
  const loggedIn = Boolean(getMemberAuth());
  const profilePhotoSize = 240;
  return <StyledBoxColumn style={{alignItems: "center"}}>
    <div style={{
      position: "relative",
      width: profilePhotoSize,
      aspectRatio: 1,
      borderRadius: profilePhotoSize / 2,
      background: "white",
      border: "4px solid black"
    }}>
      <ButtonBase onClick={() => props.onViewAvatar()}>
        <img src={props.userProfile.metadata.avatarUrl || "/images/demo_avatar.svg"}
             style={{width: "100%", height: "100%", objectFit: "cover"}}/>
      </ButtonBase>
      {isMe
        ? <Button
          color={"secondary"} variant={"contained"}
          style={{position: "absolute", width: "100%", bottom: 0, right: 0}}
          onClick={() => props.onChangeAvatar()}>
          Change Avatar
        </Button>
        : null}
    </div>
    <StyledHeadingVariant
      default_={3}
      style={{color: "white"}}>
      {UserDisplayName(member.user)}
    </StyledHeadingVariant>
    <StyledBoxRow>
      {!member.isMe()
        ? <Button variant="contained" onClick={() => {
          if (!loggedIn) {
            window.open("/login?r=" + window.location.pathname + window.location.search, "_self");
          } else {
            if (!follow) {
              const newFollow = Follow.createNew(member.memberId);
              Follows.getInstance().addFollow(newFollow)
                .then(() => setFollow(newFollow))
                .then(() => BaseApp.CONTEXT.showToast("Followed."));
            } else {
              Follows.getInstance().deleteFollow(follow)
                .then(() => setFollow(null))
                .then(() => BaseApp.CONTEXT.showToast("Unfollowed."));
            }
          }
        }}>{loggedIn && Boolean(follow) ? "Unfollow" : "Follow"}</Button>
        : null}
      {member.isMe()
        ? <Button variant="contained" onClick={() => props.path.navigate("/settings/profile")}>Edit</Button>
        : null}
    </StyledBoxRow>
  </StyledBoxColumn>;
}

export class ProfileFragment extends BaseFragment<ProfileFragmentProps, ProfileFragmentState> {

  private readonly memberAuth = getMemberAuth();

  static nestedPaths(): PathComponent[] {
    return ProfileTabsContainer.nestedPathsFromTabs(TABS);
  }

  protected onCreateState(): ProfileFragmentState {
    return {
      ...super.onCreateState(),
      background: BACKGROUNDS.find(bg => bg.id === this.props.userProfile.metadata?.backgroundId) || BACKGROUNDS[BACKGROUNDS.length - 6],
    };
  }

  private onBackground(event) {
    App.CONTEXT.showPopover(event.target, null, () =>
      <BackgroundView
        initialBackground={this.state.background}
        onChange={(background) => {
          const metadata = this.props.userProfile.metadata;
          metadata.backgroundId = background.id;
          metadata.save();
          this.setState({background});
        }}/>);
  }

  private onViewAvatar() {
    App.CONTEXT.showFullscreenDialog(() => <FullscreenContainer
      title={"View Avatar"}
      actions={[
        new Action("Close", () => App.CONTEXT.hideDialog()),
      ]}>
      <ViewAvatarFragment imageUrl={this.props.userProfile.metadata.avatarUrl || "/images/demo_avatar.svg"}/>
    </FullscreenContainer>);
  }

  private onChangeAvatar(onSaved: () => void) {
    const userProfile = this.props.userProfile;
    App.CONTEXT.showFullscreenDialog(() => <FullscreenContainer
      title={"Change Avatar"}
      actions={[
        new Action("Cancel", () => App.CONTEXT.hideDialog()),
        new Action("Save", () => {
          (async function () {
            const dom: HTMLElement = document.querySelector(
              '#avatar-preview',
            ) as HTMLElement;

            // base64 only support png svg for now, maybe more change it to Map
            const svgElement = dom.querySelector('svg');
            if (!svgElement) {
              // not generated for some reason
              return;
            }
            const svg = new XMLSerializer().serializeToString(svgElement);
            const imageURL = `data:image/svg+xml;base64,${window.btoa(svg)}`;

            const metadata = userProfile.metadata;
            metadata.avatarUrl = imageURL;
            await metadata.save();
            onSaved();
          })()
            .then(() => App.CONTEXT.showToast("Updated."))
            .catch(() => App.CONTEXT.showToast("Oops, something went wrong. Please try again."));
        }).setVariant("contained"),
      ]}>
      <AvatarPickerFragment/>
    </FullscreenContainer>);
  }

  protected renderContainerContent(): ReactElement | null {
    const editable = this.memberAuth?.getMemberId() === this.props.userProfile.member.memberId;
    return <>
      <StyledBoxColumn style={{minHeight: "60vh", position: "relative"}}>
        {this.state.background?.type === BackgroundType.IMAGE
          ? <img src={this.state.background.imageUrl} style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover"
          }}/>
          : <div style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            backgroundColor: this.state.background?.color || nightGray,
          }}/>}
        {editable
          ? <StyledBoxRow style={{
            position: "absolute",
            zIndex: 1,
            top: PD_LG,
            right: PD_LG,
            background: "white",
            borderRadius: BORDER_RADIUS_LARGE
          }}>
            <Tooltip title={"Background"}>
              <IconButton onClick={(event) => this.onBackground(event)}>
                <ColorLensOutlined/>
              </IconButton>
            </Tooltip>
          </StyledBoxRow>
          : null}
        <div style={{
          position: "absolute",
          width: "100%",
          height: "50%",
          bottom: 0,
          background: "linear-gradient(rgba(0, 0, 0, 0), rgba(0,0,0,.5))"
        }}/>
        <StyledContainer size={"xxlg"} style={{height: "100%", justifyContent: "end", padding: "4%"}}>
          <ProfileCoverView
            path={this.props.path}
            userProfile={this.props.userProfile}
            onViewAvatar={() => this.onViewAvatar()}
            onChangeAvatar={() => this.onChangeAvatar(() => {
              App.CONTEXT.hideDialog();
              this.forceUpdate();
            })}/>
        </StyledContainer>
      </StyledBoxColumn>
      <StyledContainer size={"xxlg"} style={{padding: "4%"}}>
        <ProfileTabsContainer tabs={TABS} path={this.props.path}/>
      </StyledContainer>
    </>;
  }
}